import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { setLanguageFromUserSettings as setLanguage, reverseLanguageCodeMap } from '../i18n';

interface LanguageContextType {
  isLanguageInitialized: boolean;
  applyUserLanguage: (nativeLanguage: string) => void;
  userLanguage: string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [isLanguageInitialized, setIsLanguageInitialized] = useState(false);
  const [userLanguage, setUserLanguage] = useState<string>('english'); // Default to English

  // This is the only place in the app that should call setLanguageFromUserSettings
  // with isInitialLoad=true
  const applyUserLanguage = (nativeLanguage: string) => {
    if (!isLanguageInitialized) {
      // Only do a full initialization once
      setLanguage(nativeLanguage, true);
      setIsLanguageInitialized(true);
      // Store that we've initialized language settings
      localStorage.setItem('language_initialized', 'true');
    } else {
      // For subsequent changes, don't log verbose output
      setLanguage(nativeLanguage, false);
    }

    // Update the userLanguage state
    setUserLanguage(nativeLanguage.toLowerCase());
  };

  // Check if we've initialized language on mount
  useEffect(() => {
    const initialized = localStorage.getItem('language_initialized') === 'true';
    setIsLanguageInitialized(initialized);

    // Set initial language from localStorage
    const storedLng = localStorage.getItem('i18nextLng');
    if (storedLng && reverseLanguageCodeMap[storedLng]) {
      setUserLanguage(reverseLanguageCodeMap[storedLng]);
    }
  }, []);

  const value = {
    isLanguageInitialized,
    applyUserLanguage,
    userLanguage
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}